<div class="ig-page animate-fade-in">

  <!-- ── Header ───────────────────────────────────────────────── -->
  <header class="ig-header">
    <div class="ig-header__left">
      <svg class="ig-header__icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7a2 2 0 012-2h3l1-1h4l1 1h3a2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V7zm8 3a4 4 0 100 8 4 4 0 000-8z" />
      </svg>
      <div>
        <h1 class="ig-title">Image Generation</h1>
        <p class="ig-sub">Describe your vision — the AI renders it instantly.</p>
      </div>
    </div>
    @if (isGenerating()) {
      <div class="ig-status" role="status" aria-live="polite">
        <span class="ig-status__dot"></span>
        Generating image…
      </div>
    }
  </header>

  <!-- ── Error alert ───────────────────────────────────────────── -->
  @if (errorMsg()) {
    <div class="ig-alert" role="alert">
      <div class="ig-alert__inner">
        <svg class="ig-alert__icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        </svg>
        <span>{{ errorMsg() }}</span>
      </div>
      <button class="ig-alert__close" (click)="errorMsg.set(null)" aria-label="Dismiss error">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>
  }

  <!-- ── Main layout ───────────────────────────────────────────── -->
  <div class="ig-layout">

    <!-- Chat panel -->
    <section class="ig-chat" aria-label="Chat interface">

      <div class="ig-chat__messages" aria-label="Chat messages" aria-live="polite">
        @for (m of messages(); track m.id) {
          <div class="ig-msg" [class.ig-msg--user]="m.role === 'user'" [class.ig-msg--assistant]="m.role === 'assistant'">

            <!-- Avatar -->
            <div class="ig-msg__avatar"
              [class.ig-msg__avatar--ai]="m.role === 'assistant'"
              [class.ig-msg__avatar--user]="m.role === 'user'"
              aria-hidden="true">
              @if (m.role === 'assistant') {
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              } @else {
                <span>Y</span>
              }
            </div>

            <!-- Bubble -->
            <div class="ig-msg__bubble">
              <div class="ig-msg__meta">
                <span class="ig-msg__role">{{ m.role === 'user' ? 'You' : 'AI' }}</span>
                <span class="ig-msg__time">{{ m.createdAt | date:'HH:mm' }}</span>
              </div>
              <div class="ig-msg__text">{{ m.text }}</div>

              @if (m.pending) {
                <div class="ig-typing" aria-label="Generating image">
                  <span></span><span></span><span></span>
                </div>
              }

              @if (m.imageUrl) {
                <a class="ig-msg__image-wrap" [href]="m.imageUrl" target="_blank" rel="noopener noreferrer" aria-label="Open full-size image">
                  <img class="ig-msg__image" [src]="m.imageUrl" alt="Generated image" loading="lazy"/>
                  <div class="ig-msg__image-overlay" aria-hidden="true">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                    </svg>
                    <span>Open full size</span>
                  </div>
                </a>
              }
            </div>

          </div>
        }
      </div>

      <!-- Composer -->
      <div class="ig-chat__composer">
        <div class="ig-composer__shell">
          <textarea
            class="ig-input"
            [(ngModel)]="prompt"
            placeholder="Describe an image… e.g. 'cinematic portrait, golden hour, shallow depth of field, film grain'"
            rows="3"
            [disabled]="isGenerating()"
            aria-label="Image generation prompt"
          ></textarea>
          <button class="ig-send" (click)="generate()" [disabled]="!canGenerate()" aria-label="Generate image">
            @if (isGenerating()) {
              <span class="ig-send__spinner" aria-hidden="true"></span>
            } @else {
              <svg class="ig-send__icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
            }
          </button>
        </div>
        <p class="ig-hint">
          @if (isGenerating()) {
            <span class="ig-hint__dot" aria-hidden="true"></span>
            Rendering your image…
          } @else {
            Tip: add "cinematic", "8K", or an art style like "oil painting" for better results.
          }
        </p>
      </div>
    </section>

    <!-- Gallery panel -->
    <aside class="ig-gallery" aria-label="Recent generations">
      <div class="ig-gallery__header">
        <div class="ig-gallery__title-wrap">
          <div class="ig-gallery__icon-wrap" aria-hidden="true">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
          </div>
          <h2 class="ig-gallery__title">Recent</h2>
        </div>
        <div class="ig-gallery__actions">
          <a routerLink="/image-library" class="ig-gallery__see-all">See all</a>
          @if (isLoadingHistory()) {
            <span class="ig-gallery__badge">…</span>
          } @else {
            <span class="ig-gallery__badge">{{ recentImages().length }}</span>
          }
        </div>
      </div>

      @if (recentImages().length === 0) {
        <div class="ig-empty">
          <div class="ig-empty__icon-wrap" aria-hidden="true">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.3">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
          </div>
          <p class="ig-empty__title">No images yet</p>
          <p class="ig-empty__sub">Generated images will appear here. Send your first prompt to begin.</p>
        </div>
      } @else {
        <div class="ig-grid">
          @for (img of recentImages(); track img.generation_id) {
            <div class="ig-card">
              <a [href]="img.image_url" target="_blank" rel="noopener noreferrer" class="ig-card__imgwrap" aria-label="View generated image in full size">
                <img [src]="img.image_url" alt="Generated image" loading="lazy" class="ig-card__img"/>
                <div class="ig-card__overlay" aria-hidden="true">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                  </svg>
                </div>
              </a>
            </div>
          }
        </div>
      }
    </aside>

  </div>
</div>
